import { useMemo } from 'react';

import { DataSourceInstanceSettings } from '@grafana/data';

import { StringParam, useQueryParam } from 'use-query-params';

import { useLastSelectedStorage } from './useStorage';
import { useSupportedDatasources } from './useSupportedDatasources';

/**
 * A custom hook that determines the initial datasource to use.
 *
 * This hook follows a priority order to select the datasource:
 * 1. Uses the datasource specified in the URL query parameter 'ds' if it exists and is valid.
 * 2. Uses the last selected datasource if available.
 * 3. Uses the default datasource if one is set.
 * 4. Falls back to the last datasource in the list if none of the above are available.
 *
 * @returns {Object} The selected datasource object.
 */
export function useInitialDatasource(): DataSourceInstanceSettings {
  const datasources = useSupportedDatasources();
  const [qsDatasourceId] = useQueryParam('ds', StringParam);
  const [lastSelected] = useLastSelectedStorage();
  return useMemo(() => {
    // If the datasource is passed in as a query parameter, use that first.
    if (qsDatasourceId !== null && qsDatasourceId !== undefined) {
      const found = datasources.find((x) => x.uid === qsDatasourceId);
      if (found != null) {
        return found;
      }
    }
    // If a datasource has been used previously use that next.
    if (lastSelected != null) {
      const found = datasources.find((x) => x.uid === lastSelected);
      if (found != null) {
        return found;
      }
    }
    // Find the default datasource and use that if it exists.
    const found = datasources.find((x) => x.isDefault === true);
    if (found != null) {
      return found;
    }
    // Finally, use the last datasource in the list.
    return datasources[datasources.length - 1];
  }, [lastSelected, qsDatasourceId, datasources]);
}
